import { transaction } from "mobx";
import { v4 } from "uuid";

import type { PlaygroundStore } from "@/components/Playground/playground-store";
import {
  PLAYGROUND_DRAWER_MODES,
  PLAYGROUND_SESSION_MODES,
  PLAYGROUND_TYPES,
  VariableSet,
} from "@/components/Playground/types";

export const TOUR_VARIABLE_NAMES = ["document_type", "jurisdiction", "document_excerpt"] as const;

export const TOUR_SYSTEM_MESSAGE =
  "You are a legal research assistant specializing in document analysis. You parse legal documents, extract key clauses and precedents, and find related cases from case law databases. Always cite specific sections and provide confidence scores for your matches.";

export const TOUR_USER_MESSAGE_JINJA2 = `Analyze the following legal document and find connected cases:

Document Type: {{ document_type }}
Jurisdiction: {{ jurisdiction }}

Excerpt:
{{ document_excerpt }}
Identify the key legal principles, extract relevant clauses, and find related precedents.`;

export const TOUR_USER_MESSAGE_FSTRING = `Analyze the following legal document and find connected cases:

Document Type: {document_type}
Jurisdiction: {jurisdiction}

Excerpt:
{document_excerpt}
Identify the key legal principles, extract relevant clauses, and find related precedents.`;

export const TOUR_VARIABLE_SET_NAME = "Contract Dispute";

export const TOUR_VARIABLE_VALUES: Record<(typeof TOUR_VARIABLE_NAMES)[number], string> = {
  document_type: "Employment Contract",
  jurisdiction: "California, USA",
  document_excerpt:
    "Section 12.3: Non-compete clause states that the employee shall not engage in competing business activities within a 50-mile radius for a period of 24 months following termination of employment. Liquidated damages of $50,000 apply per violation.",
};

export const TOUR_FAKE_OUTPUT_TEXT = `**Analysis Summary**

**Document Type:** Employment Contract (California, USA)
**Key Clause:** Non-compete (Section 12.3) — 50-mile radius, 24-month duration, $50,000 liquidated damages.

**Legal Principles Identified:**
1. Non-compete enforceability under California Business & Professions Code §16600
2. Reasonableness of geographic and temporal scope
3. Liquidated damages vs. unenforceable penalty doctrine

**Connected Cases (3 highest-confidence matches):**
1. *Edwards v. Arthur Andersen LLP* (2008) — California Supreme Court ruling that non-compete agreements are generally void under §16600. **Confidence: 0.94**
2. *Application Group, Inc. v. Hunter Group, Inc.* (1998) — Application of §16600 to out-of-state employees. **Confidence: 0.81**
3. *Robinson v. Jardine Insurance Brokers* (1997) — Liquidated damages provisions in employment agreements. **Confidence: 0.72**

**Recommendation:** This non-compete clause is likely unenforceable under California law per Edwards v. Arthur Andersen. Consider redrafting to a confidentiality / non-solicitation agreement, which remains enforceable.`;

const buildTourVariableSet = (): VariableSet => ({
  id: v4(),
  name: TOUR_VARIABLE_SET_NAME,
  variables: TOUR_VARIABLE_NAMES.map(key => ({
    id: v4(),
    key,
    value: TOUR_VARIABLE_VALUES[key],
  })),
});

const getUserMessageForFormat = (templateFormat: string): string =>
  templateFormat === "f-string" ? TOUR_USER_MESSAGE_FSTRING : TOUR_USER_MESSAGE_JINJA2;

/**
 * Applies the tour demo content to the playground store.
 * - Switches to chat mode + batch session
 * - Replaces messages with the legal-document-parsing demo (system + user)
 * - Seeds detected input variables (skips API delay)
 * - Replaces input variable sets with one pre-filled "Contract Dispute" set
 *
 * No state is captured for restore — the standalone playground is intentionally
 * pre-filled for the tour, and the demo content stays after the tour ends.
 */
export const applyTourPreconfig = (store: PlaygroundStore): void => {
  const templateFormat = store.template_format;
  const userMessageText = getUserMessageForFormat(templateFormat);

  transaction(() => {
    store.setMode(PLAYGROUND_TYPES.CHAT);
    store.setPlaygroundView(PLAYGROUND_SESSION_MODES.BATCH, PLAYGROUND_DRAWER_MODES.OUTPUT);

    // Replace messages: clear existing, add system + user
    const existingIds = store.messages.map(msg => msg.id);
    existingIds.forEach(id => store.deleteMessage(id));

    store.addMessage({
      id: v4(),
      role: "system",
      content: [{ type: "text", text: TOUR_SYSTEM_MESSAGE }],
    });
    store.addMessage({
      id: v4(),
      role: "user",
      content: [{ type: "text", text: userMessageText }],
    });

    // Seed detected variables to skip the debounced API roundtrip
    store.setDetectedInputVariables([...TOUR_VARIABLE_NAMES]);

    // Replace input variable sets with the demo set
    const existingSetIds = store.input_variable_sets.map(set => set.id);
    existingSetIds.forEach(id => store.removeInputVariableSet(id));
    store.addInputVariableSet(buildTourVariableSet());
  });
};
